import type { ObjectColorPalette } from "./colorOverrides";
import type { RenderState } from "./renderSlice";

/** V8 dictionary-mode Record entry (see propertyTreeState). */
const RECORD_ENTRY_BYTES = 32;
/** Packed Uint32Array of object ids in a color bucket. */
const UINT32_BYTES_PER_ID = 4;

export type PaletteMemoryStats = {
    objectCount: number;
    bucketCount: number;
    bucketIdCount: number;
    approxBytes: number;
};

export type ColorOverridesMemoryStats = {
    default: PaletteMemoryStats;
    xray: PaletteMemoryStats;
    selected: PaletteMemoryStats;
    approxBytes: number;
};

function paletteStats(palette: ObjectColorPalette): PaletteMemoryStats {
    let bucketCount = 0;
    let bucketIdCount = 0;
    // for-in instead of Object.keys() so a scene-scale map does not allocate a
    // key array just to be counted during a diagnostics reading.
    for (const key in palette) {
        bucketCount++;
        bucketIdCount += palette[key].ids.size;
    }
    return {
        objectCount: bucketIdCount,
        bucketCount,
        bucketIdCount,
        // packed id arrays + one Record slot per bucket (vec lives on the bucket).
        approxBytes: bucketIdCount * UINT32_BYTES_PER_ID + bucketCount * RECORD_ENTRY_BYTES,
    };
}

/** Byte-attributable stats for renderSlice.colorOverrides. Cheap; no copies. */
export function colorOverridesMemoryStats(overrides: RenderState["colorOverrides"]): ColorOverridesMemoryStats {
    const defaultStats = paletteStats(overrides.default);
    const xray = paletteStats(overrides.xray);
    const selected = paletteStats(overrides.selected);
    return {
        default: defaultStats,
        xray,
        selected,
        approxBytes: defaultStats.approxBytes + xray.approxBytes + selected.approxBytes,
    };
}
